import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LOGO_ICON } from "../assets/brand";
import { Clock, MapPin, CheckCircle2, HeartHandshake } from "lucide-react";

const CONTACT_TYPES = [["evangelism", "Evangelism / Outreach"], ["event_visitor", "Event Visitor"], ["other", "Other"]];
const INPUT = "w-full rounded-xl border px-4 py-3 text-[15px] outline-none focus:ring-2 focus:ring-[#0B5E4E]/30";

const VISITOR_CHOICES = [
  { v: "first_time", label: "I'm new here – first time", sub: "I'd like to get to know the church" },
  { v: "just_visiting", label: "I'm just visiting", sub: "Here for a visit or event" },
];

export default function Connect({ mode = "evangelism" }) {
  const visitor = mode === "visitor";
  const [searchParams] = useSearchParams();
  const source = searchParams.get("src") === "qr" ? "qr" : "";
  const [f, setF] = useState({ name: "", phone: "", email: "", contact_type: "", contact_type_other: "", address: "", postcode: "", notes: "" });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [resp, setResp] = useState(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim()) return toast.error("Please fill in your name and phone number.");
    if (!f.contact_type) return toast.error(visitor ? "Please tell us if you're new here or just visiting." : "Please choose a contact type.");
    if (f.contact_type === "other" && !f.contact_type_other.trim()) return toast.error("Please type your contact type.");
    if (!agreed) return toast.error("Please agree to the Privacy & Data Protection Policy.");
    setLoading(true);
    try {
      const { data } = await api.post("/public/connect", { ...f, privacy_consent: true, source });
      setResp(data?.response || null);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="connect-page" style={{ background: "var(--sj-bg)" }}>
      {/* Brand cover */}
      <div className="relative hidden lg:block" style={{ background: "linear-gradient(140deg, #063B30, #0B5E4E)" }}>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Streams of Joy Erith London" className="h-16 w-16 rounded-2xl shadow-lg" />
            <div className="leading-tight">
              <div className="font-head font-extrabold text-lg">Streams of Joy</div>
              <div className="text-sm font-semibold uppercase tracking-[0.2em] text-white/80">Erith London</div>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.3em] mb-3" style={{ color: "#F5C518" }}>Welcome</div>
            <h1 className="font-head text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">We'd love to connect with you</h1>
            <p className="mt-5 text-white/90 text-lg max-w-md font-semibold">What God cannot do does not exist!</p>
            <div className="mt-8 space-y-3 max-w-md">
              <div className="flex items-start gap-3 rounded-xl px-4 py-3" style={{ background: "rgba(255,255,255,0.08)" }}>
                <Clock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#F5C518" }} />
                <div className="text-sm">
                  <div className="font-semibold text-white">Service Times</div>
                  <div className="text-white/80">Wednesday 7:00pm</div>
                  <div className="text-white/80">Sunday 10:00am &amp; 12:00pm</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#F5C518" }} />
                <p className="text-white/85 text-sm">5 Pier Road, Erith, London DA8 1TA</p>
              </div>
            </div>
          </div>
          <div className="text-white/60 text-xs">Streams of Joy Erith London · Church Management System</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {done ? (
            <div className="text-center py-10" data-testid="connect-success">
              <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-5" style={{ background: "#0B5E4E1A" }}>
                <CheckCircle2 className="h-9 w-9" style={{ color: "var(--sj-primary)" }} />
              </div>
              <h2 className="font-head text-2xl font-extrabold" style={{ color: "var(--sj-text)" }}>Thank you!</h2>
              <p className="mt-3 text-[15px]" style={{ color: "var(--sj-text-2)" }}>
                {resp?.enabled && resp?.message
                  ? resp.message
                  : "We've received your details and a member of our team will reach out to you soon. We can't wait to meet you."}
              </p>
              {resp?.enabled && resp?.info_qr_enabled && resp?.info_qr_link && (
                <div className="mt-6 flex flex-col items-center" data-testid="connect-info-qr">
                  <div className="p-3.5 rounded-2xl border-2 bg-white" style={{ borderColor: "var(--sj-primary)" }}>
                    <QRCodeCanvas value={resp.info_qr_link} size={160} level="M" fgColor="#0B5E4E" />
                  </div>
                  <a href={resp.info_qr_link} target="_blank" rel="noreferrer" className="mt-3 font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>
                    {resp.info_qr_label || "More about our church"}
                  </a>
                  <p className="mt-1 text-xs" style={{ color: "var(--sj-text-3)" }}>Scan or tap to learn more</p>
                </div>
              )}
              <a href="https://www.sojlondon.org" className="inline-block mt-6 font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Visit our website</a>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1" style={{ color: "var(--sj-primary)" }}>
                <HeartHandshake className="h-5 w-5" />
                <span className="text-sm font-semibold uppercase tracking-wider" data-testid="connect-eyebrow">{visitor ? "First timers & visitors — Connect with us" : "Evangelism — Connect with us"}</span>
              </div>
              <h2 className="font-head text-3xl font-extrabold" style={{ color: "var(--sj-text)" }} data-testid="connect-title">{visitor ? "Welcome! Tell us about you" : "Tell us about you"}</h2>
              <p className="mt-2 text-sm" style={{ color: "var(--sj-text-2)" }}>Leave your details and we'll get in touch. It only takes a minute.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input data-testid="connect-name" placeholder="Full name *" value={f.name} onChange={upd("name")} className={INPUT} style={{ borderColor: "var(--sj-border)" }} />
                <input data-testid="connect-phone" placeholder="Phone number *" value={f.phone} onChange={upd("phone")} className={INPUT} style={{ borderColor: "var(--sj-border)" }} />
                <input data-testid="connect-email" type="email" placeholder="Email address (optional)" value={f.email} onChange={upd("email")} className={INPUT} style={{ borderColor: "var(--sj-border)" }} />
                {visitor ? (
                  <div data-testid="connect-visitor-choice">
                    <p className="text-sm font-semibold mb-2" style={{ color: "var(--sj-text)" }}>Are you new to the church? <span style={{ color: "#B42318" }}>*</span></p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {VISITOR_CHOICES.map((o) => {
                        const on = f.contact_type === o.v;
                        return (
                          <button key={o.v} type="button" data-testid={`connect-choice-${o.v}`} onClick={() => setF({ ...f, contact_type: o.v })}
                            className="text-left rounded-xl border px-4 py-3 bg-white transition-all"
                            style={on ? { borderColor: "var(--sj-primary)", boxShadow: "0 0 0 2px rgba(11,94,78,0.15)" } : { borderColor: "var(--sj-border)" }}>
                            <span className="block text-sm font-semibold" style={{ color: on ? "var(--sj-primary)" : "var(--sj-text)" }}>{o.label}</span>
                            <span className="block text-xs" style={{ color: "var(--sj-text-3)" }}>{o.sub}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                <select data-testid="connect-contact-type" required value={f.contact_type} onChange={upd("contact_type")} className={INPUT} style={{ borderColor: "var(--sj-border)", color: f.contact_type ? "var(--sj-text)" : "var(--sj-text-3)" }}>
                  <option value="" disabled>Contact type *</option>
                  {CONTACT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                )}
                {!visitor && f.contact_type === "other" && (
                  <input data-testid="connect-contact-type-other" required maxLength={120} autoFocus placeholder="Please type your contact type *" value={f.contact_type_other} onChange={upd("contact_type_other")} className={INPUT} style={{ borderColor: "var(--sj-border)" }} />
                )}
                <input data-testid="connect-address" placeholder="Address (optional)" value={f.address} onChange={upd("address")} className={INPUT} style={{ borderColor: "var(--sj-border)" }} />
                <input data-testid="connect-postcode" placeholder="Postcode (optional)" value={f.postcode} onChange={upd("postcode")} className={INPUT} style={{ borderColor: "var(--sj-border)" }} />
                <textarea data-testid="connect-notes" placeholder="Anything you'd like us to know? (optional)" value={f.notes} onChange={upd("notes")} rows={3} className={INPUT} style={{ borderColor: "var(--sj-border)" }} />
                <label className="flex items-start gap-2.5 text-sm cursor-pointer" style={{ color: "var(--sj-text-2)" }}>
                  <input data-testid="connect-privacy-checkbox" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 rounded accent-[#0B5E4E] shrink-0" />
                  <span>I agree to the <Link to="/privacy" target="_blank" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Privacy &amp; Data Protection Policy</Link>. <span style={{ color: "#B42318" }}>*</span></span>
                </label>
                {(!agreed || !f.contact_type) && <p data-testid="connect-required-hint" className="text-xs -mb-1" style={{ color: "var(--sj-text-3)" }}>{visitor ? "Choosing new here or just visiting" : "Contact type"} and agreeing to the Privacy &amp; Data Protection Policy are required.</p>}
                <button type="submit" data-testid="connect-submit" disabled={loading || !agreed || !f.contact_type || (f.contact_type === "other" && !f.contact_type_other.trim())} className="w-full rounded-full py-3 font-semibold text-white shadow-sm transition-all hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
                  {loading ? "Sending..." : "Send my details"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
